import { BREAKPOINTS } from '@app/constants/breakpoints';
import { useIsScreenSize } from '@app/utils/hooks';
import { isNull } from '@app/utils/is';
import React from 'react';
import { Placement, useLayer, UseLayerProps } from 'react-laag';

interface Context {
  closeDropdown: () => void;
  isDropdownOpen: boolean;
  isMobile: boolean;
  activeParentItemKey: string | null;
  layerProps: UseLayerProps;
  menuId: string;
  openDropdown: () => void;
  setActiveParentItemKey: (key: string | null) => void;
  toggleDropdown: () => void;
  triggerRef: React.RefObject<HTMLButtonElement>;
}

export const useDropdownContext = (): Context => {
  const context = React.useContext(DropdownContext);
  if (isNull(context)) {
    throw new Error('DropdownContext can not be used outside the scope of DropdownContextProvider');
  }

  return context;
};

export const DropdownContext = React.createContext<Context | null>(null);

interface DropdownContextProviderProps {
  placement?: Placement;
  onOpen?: () => void;
  onClose?: () => void;
}

export const DropdownContextProvider = ({
  children,
  placement = 'bottom-center',
  onClose,
  onOpen,
}: React.WithChildren<DropdownContextProviderProps>) => {
  const menuId = React.useId();
  const triggerRef = React.useRef<HTMLButtonElement>(null);

  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const [activeParentItemKey, setActiveParentItemKey] = React.useState<string | null>(null);

  const isMobile = useIsScreenSize(BREAKPOINTS.SM);

  const openDropdown = React.useCallback(() => {
    onOpen?.();
    setIsDropdownOpen(true);
  }, [onOpen]);

  const closeDropdown = React.useCallback(() => {
    setIsDropdownOpen(false);
    setActiveParentItemKey(null);
    triggerRef.current?.focus({ preventScroll: true });
    onClose?.();
  }, [onClose]);

  const toggleDropdown = React.useCallback(() => {
    isDropdownOpen ? closeDropdown() : openDropdown();
  }, [closeDropdown, isDropdownOpen, openDropdown]);

  const isOpen = isDropdownOpen && !isMobile;

  const layerProps = useLayer({
    auto: true,
    isOpen,
    onDisappear: type => {
      if (type === 'full') {
        closeDropdown();
      }
    },
    onOutsideClick: closeDropdown,
    placement,
    possiblePlacements: ['top-center', 'top-end', 'top-start', 'bottom-center', 'bottom-end', 'bottom-start'],
    preferY: 'bottom',
    triggerOffset: 8,
  });

  return (
    <DropdownContext.Provider
      value={{
        activeParentItemKey,
        closeDropdown,
        isDropdownOpen,
        isMobile,
        layerProps,
        menuId,
        openDropdown,
        setActiveParentItemKey,
        toggleDropdown,
        triggerRef,
      }}
    >
      {children}
    </DropdownContext.Provider>
  );
};
